/**
 * FilterModule
 *
 * Manages product filtering interactions, DOM updates, and UI visibility.
 * This module handles filter input changes, resets filters, and updates the
 * product list based on applied filters.
 *
 * @module FilterModule
 */
(function () {
  'use strict';

  // Public API for external use
  window.POV.filter = {
    apply: applyFilters,
    reset: resetFilters,
    openPanel: openFilterPanel,
    closePanel: closeFilterPanel
  };

  /**
   * DOM selectors used in the FilterModule.
   * @constant {Object}
   */
  const SELECTORS = {
    host: '#shopify-section-collection-products',
    inputs: '[data-filter-input]',
    priceInputs: '[name="filter.v.price.gte"], [name="filter.v.price.lte"]',
    sortInputs: '[data-filter-sort]',
    collectionHeader: '[data-collection-header]',
    panel: '[data-filter-drawer]',
    scrim: '#Scrim',
    reset: '[data-filter-reset]',
    open: '[data-filter-trigger]',
    close: '[data-filter-close], #Scrim',
    trustPilotWidget: '.trustpilot-widget',
  };

    /**
   * Events emited by the FilterModule.
   * @constant {Object}
   */
    const EVENTS = {
      change: 'filter:change',
      reset: 'filter:reset',
      open: 'filter:open',
      close: 'filter:close'
    };

  // Cache DOM elements
  const filterInputNodes = Array.from(document.querySelectorAll(SELECTORS.inputs));
  const priceInputNodes = Array.from(document.querySelectorAll(SELECTORS.priceInputs));
  const sortInputNodes = Array.from(document.querySelectorAll(SELECTORS.sortInputs));
  const collectionHeaderElement = document.querySelector(SELECTORS.collectionHeader);
  const panelElement = document.querySelector(SELECTORS.panel);
  const scrimElement = document.querySelector(SELECTORS.scrim);

  // Add debounced input event listeners to filter inputs
  filterInputNodes.forEach((inputNode) => {
    const delay = inputNode.type === 'number' ? 500 : 200;
    const debouncedApplyFilters = debounce(applyFilters, delay);
    inputNode.addEventListener('input', debouncedApplyFilters);
  });

  // Add event listeners for price input fields
  priceInputNodes.forEach((input) => {
    // XXX handle iOS select issue
    // input.addEventListener('mouseup', handlePriceInputFocus);
    input.addEventListener('keyup', handlePriceInputEnter);
  });

  // Add event listeners for reset, open, and close actions
  document.querySelectorAll(SELECTORS.reset).forEach((button) => {
    button.addEventListener('click', resetFilters);
  });

  document.querySelectorAll(SELECTORS.close).forEach((button) => {
    button.addEventListener('click', closeFilterPanel);
  });

  document.querySelectorAll(SELECTORS.open).forEach((button) => {
    button.addEventListener('click', openFilterPanel);
  });

  // Listen for browser navigation events (back/forward)
  window.addEventListener('popstate', handlePopState);

  // Listen for viewport changes and apply appropriate filter panel states
  window.addEventListener('resize', handleMediaLogic);
  window.addEventListener('load', handleMediaLogic);

  function handleMediaLogic(event) {
    const mql = window.matchMedia(`(min-width: ${POV.Breakpoints.large})`);
    const isLarge = mql.matches;
    const collapsibles = POV.Collapsible.filter(collapsible => collapsible.trigger.closest('[data-filter]'));

    collapsibles.forEach(collapsible => {
      if (isLarge) {
        const isExpandedDesktop = collapsible.panel.getAttribute('data-collapsible-expanded-desktop') === 'true';
        isExpandedDesktop ? collapsible.expand() : collapsible.collapse();
        closeFilterPanel();
      } else {
        if (event.type === 'load') {
          const isExpandedMobile = collapsible.panel.getAttribute('data-collapsible-expanded-mobile') === 'true';
          isExpandedMobile ? collapsible.expand() : collapsible.collapse(); 
        }
      }
    });

    // Fix accessibility issues with hidden filter panel on desktop
    if (event.type === 'load') panelElement.setAttribute('aria-hidden', isLarge ? 'false' : 'true');
  }

  /**
   * Applies the selected filters, updates the URL, and fetches the filtered product list.
   *
   * @async
   * @function applyFilters
   * @returns {Promise<void>}
   */
  async function applyFilters() {
    // Gather active filter inputs (checked, price, and sort fields)
    const checkedInputs = document.querySelectorAll(`${SELECTORS.inputs}:checked`);
    const activeInputNodes = [...checkedInputs, ...priceInputNodes, ...sortInputNodes];

    const params = new URLSearchParams();
    activeInputNodes.forEach((input) => {
      if (input.value) {
        params.append(input.name, input.value);
      }
    });

    // Indicate loading state and hide the collection header if present.
    const productContainer = document.querySelector(SELECTORS.host);
    productContainer.classList.add('filter-loading');
    if (collectionHeaderElement) {
      collectionHeaderElement.classList.add('hidden');
    }

    // Update the URL with the current filters.
    const newUrl = createUrl(params);
    updateUrl(newUrl);

    // Fetch filtered product list
    try {
      const response = await fetch(`${window.location.pathname}?${params.toString()}&sections=collection-products`);
      const data = await response.json();
      updateProductList(data);
      reinitTrustPilotWidgets();
      POV.emit(EVENTS.change, window.POV.filter);
    } catch (error) {
      console.error('Error fetching filtered products:', error);
    }
  }

  /**
   * Updates the product list section with new HTML content.
   *
   * @function updateProductList
   * @param {Object} responseData - JSON response containing updated HTML content.
   */
  function updateProductList(responseData) {
    const productContainer = document.querySelector(SELECTORS.host);
    productContainer.outerHTML = responseData['collection-products'];
  }

  /**
   * Constructs a new URL with the provided query parameters.
   *
   * @function createUrl
   * @param {URLSearchParams} params - The query parameters.
   * @returns {string} The newly constructed URL.
   */
  function createUrl(params) {
    const baseUrl = window.location.href.split('?')[0];
    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * Updates the browser's URL without reloading the page.
   *
   * @function updateUrl
   * @param {string} url - The new URL to be pushed to the history.
   */
  function updateUrl(url) {
    window.history.pushState(null, '', url);
  }

  /**
   * Resets all filter inputs to their initial state and reapplies the filters.
   *
   * @function resetFilters
   */
  function resetFilters() {
    filterInputNodes.forEach((input) => {
      const initialValue = input.getAttribute('data-filter-initial-value');
      if (input.type === 'radio' || input.type === 'checkbox') {
        input.checked = initialValue === 'checked';
      } else if (input.type === 'number' || input.type === 'text') {
        input.value = initialValue || '';
      }
    });

    POV.emit(EVENTS.reset, window.POV.filter);
    applyFilters();
  }

  /**
   * Opens the filter UI panel by setting the appropriate ARIA attributes.
   *
   * @function openFilterPanel
   */
  function openFilterPanel() {
    panelElement.setAttribute('aria-hidden', 'false');
    scrimElement.setAttribute('aria-hidden', 'false');
    POV.emit(EVENTS.open, window.POV.filter);
  }

  /**
   * Closes the filter UI panel by setting the appropriate ARIA attributes.
   *
   * @function closeFilterPanel
   */
  function closeFilterPanel() {
    panelElement.setAttribute('aria-hidden', 'true');
    scrimElement.setAttribute('aria-hidden', 'true');
    POV.emit(EVENTS.close, window.POV.filter);
  }

  /**
   * Handles the browser's popstate event by reapplying filters based on URL parameters.
   *
   * @function handlePopState
   */
  function handlePopState() {
    const params = new URLSearchParams(document.location.search);

    // Reset all checked filter inputs.
    const checkedInputs = document.querySelectorAll(`${SELECTORS.inputs}:checked`);
    checkedInputs.forEach((input) => (input.checked = false));

    // Update filter inputs based on the URL parameters.
    for (const [name, value] of params.entries()) {
      const inputElement =
        document.querySelector(`[name='${name}'][value='${value}']`) ||
        document.querySelector(`[name='${name}']`);
      if (!inputElement) continue;

      if (inputElement.type === 'radio' || inputElement.type === 'checkbox') {
        inputElement.checked = true;
      } else if (inputElement.type === 'number') {
        inputElement.value = value;
      }
    }

    applyFilters();
  }

  /**
   * Trustpilot widgets need to be reinited after DOM update.
   *
   * @function reinitTrustPilotWidgets
   */
  function reinitTrustPilotWidgets() {
    try {
      if (!window.Trustpilot || typeof window.Trustpilot.loadFromElement !== 'function') return;

      const trustPilotWidgetNodes = Array.from(document.querySelectorAll(SELECTORS.trustPilotWidget));

      trustPilotWidgetNodes.forEach(widget => {
        window.Trustpilot.loadFromElement(widget);
      });
    } catch (error) {
      console.error('Error reiniting Trustpilot widgets:', error);
    }
  }

  /**
   * On focus, select the entire value of a price input field.
   *
   * @function handlePriceInputFocus
   */
  function handlePriceInputFocus(event) {
    event.target.select();
  }

  /**
   * On mobile, pressing Enter on a price input field will blur the input, closing the keyboard.
   *
   * @function handlePriceInputEnter
   */
  function handlePriceInputEnter(event) {
    const input = event.target;
    if (event.key === 'Enter') input.blur();
  }
})();
